import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';

/**
 * Um critério de ordenação oferecido no modo card.
 *
 * <p>Genérico no campo para a tela não perder a tipagem: quem declara
 * `sortField = signal<'nome' | 'data'>(...)` usa `CardSortOption<'nome' | 'data'>[]` e o
 * compilador continua barrando campo inexistente.</p>
 */
export interface CardSortOption<TField extends string = string> {
    /** Campo enviado na ordenação — o mesmo nome usado pela tabela/API. */
    field: TField;
    /** Rótulo exibido na pill. */
    label: string;
}

/** Direção da ordenação: 1 crescente, -1 decrescente. */
export type CardSortOrder = 1 | -1;

/**
 * Pills de ordenação do modo card — a única forma de ordenar quando não há cabeçalho de tabela
 * para clicar.
 *
 * <p>Vive num componente próprio porque o mesmo desenho é usado pelo `app-custom-table` e pelas
 * telas que montam a grade de cards à mão; duplicar levaria a pills com aparência e comportamento
 * diferentes entre telas.</p>
 *
 * <p>Clicar na pill ativa ordena por ela em ordem crescente; clicar de novo inverte. O estado é
 * de duas vias (`[(field)]` / `[(order)]`) para a tela reagir e recarregar.</p>
 */
@Component({
    selector: 'app-card-sort-pills',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './card-sort-pills.html',
    styleUrl: './card-sort-pills.scss'
})
export class CardSortPills {
    /** Critérios disponíveis, na ordem em que aparecem. */
    opcoes = input.required<CardSortOption[]>();

    /** Campo ordenado no momento. */
    field = model<string>('');

    /** Direção do campo ordenado. */
    order = model<CardSortOrder>(1);

    /** Rótulo do grupo para leitores de tela. */
    ariaLabel = input('Ordenação dos cards');

    protected readonly ativo = computed(() => this.field());

    protected isAtivo(field: string): boolean {
        return this.field() === field;
    }

    /**
     * Alterna a direção quando a pill já está ativa; em pill nova começa sempre crescente, para o
     * usuário não herdar a direção do critério anterior sem perceber.
     */
    protected onPillClick(field: string): void {
        if (this.isAtivo(field)) {
            this.order.set(this.order() === 1 ? -1 : 1);
            return;
        }

        this.field.set(field);
        this.order.set(1);
    }

    /** Seta para cima em pill inativa: indica que é clicável sem afirmar direção em vigor. */
    protected iconeClass(field: string): string {
        if (!this.isAtivo(field)) {
            return 'pi pi-arrow-up';
        }
        return this.order() === 1 ? 'pi pi-arrow-up' : 'pi pi-arrow-down';
    }

    protected aria(label: string, field: string): string {
        if (!this.isAtivo(field)) {
            return `Ordenar por ${label}`;
        }
        const proxima = this.order() === 1 ? 'decrescente' : 'crescente';
        return `Ordenar por ${label} em ordem ${proxima}`;
    }
}
